import { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import CollapseButton from './collapseButton';
import customIcons from '../../utils/icons';
import { MenuItem } from '../../types/global';
import { internalAdminRoles, wardDataAdminRoles } from '../../types/enums';
import MenuItems from './menuItems';
import Logo from '../logo';
import useAuth from '../../utils/auth';
import getBackendUrl from '../../utils/backendUrl';
import useMainMenuConfig from './useMainMenuConfig';
import { HmrcDataToggleContainer } from '../../features/hmrc-data-toggle';

export default function MainMenu() {
  const authUser = useAuth();
  const [isExpanded, setIsExpanded] = useState(window.innerWidth >= 768);
  const [userToggled, setUserToggled] = useState(false);
  const { shutdown } = useIntercom();

  const clientServicesOrAbove = internalAdminRoles.includes(authUser.role);
  const wardAdminOrAbove = wardDataAdminRoles.includes(authUser.role);

  const toggleMenu = () => {
    setIsExpanded(!isExpanded);
    setUserToggled(true);
  };

  const expandMenu = () => {
    setIsExpanded(true);
    setUserToggled(true);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!userToggled) {
        if (window.innerWidth < 768) {
          setIsExpanded(window.innerWidth >= 768);
        }
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [userToggled]);

  const navigationItems: MenuItem[] = useMainMenuConfig();

  const supportItems: MenuItem[] = [
    ...(clientServicesOrAbove ? [{
      name: 'Admin', icon: customIcons.admin, path: '/admin', enabled: true,
    }, {
      name: 'Support', icon: customIcons.supportIcon, path: clientServicesOrAbove ? '/support' : '/support/product', enabled: true,
    }] : []),
    {
      name: 'Settings', icon: customIcons.settingsIcon, path: '/settings', enabled: wardAdminOrAbove,
    },
  ];
  return (
    <div
      id="side-menu"
      className={`sm:block h-screen p-4 bg-white outline outline-pipgrey-light relative ${isExpanded ? 'min-w-full ' : 'min-w-min'}`}
    >
      <div id="main-menu-container" className="flex h-full min-h-0 flex-col">
        <div id="menu-logo-container" className={`pb-6 ${isExpanded ? 'pt-3 px-3' : 'pt-3'}`}>
          {isExpanded
            ? <Logo src="/pip_green_logo.svg" alt="Policy in Practice" />
            : <Logo src="/pip_green_logo_notext.svg" alt="Policy in Practice" />}
        </div>

        <nav className="flex h-full w-full flex-col font-nunito">
          <ul
            id="navigation-menuitems"
            className="flex flex-col justify-between gap-1"
          >
            {navigationItems.map((item) => (
              <MenuItems
                key={item.name}
                item={item}
                isExpanded={isExpanded}
                className="flex flex-row items-center p-2"
              />
            ))}
          </ul>
          <div className="flex flex-col gap-2 mt-auto mb-2">
            <HmrcDataToggleContainer isExpanded={isExpanded} onExpand={expandMenu} />
            <ul
              id="support-menuitems"
              className="flex flex-col text-sm"
            >
              {supportItems.map((menuItem) => (
                <MenuItems
                  item={menuItem}
                  key={menuItem.name}
                  isExpanded={isExpanded}
                  className="flex flex-row items-center px-2 py-1"
                />
              ))}
            </ul>
          </div>
        </nav>
        <div id="user-info-container" className={`border-t py-2 flex justify-center text-sm ${isExpanded ? 'gap-2 items-center ' : 'grow '}`}>
          {authUser.email && isExpanded ? (
            <span className="px-2 pt-3 justify-center text-piptext-strong text-sm font-normal truncate text-ellipsis max-w-[150px] leading-5">{authUser.email}</span>) : null}
          <button
            id="logout-button"
            type="button"
            className={`hover:cursor-pointer text-lg text-piptext-body ${isExpanded ? 'self-end' : ''}`}
            onClick={() => {
              shutdown();
              window.location.href = `${getBackendUrl()}/backend/auth/logout`;
            }}
          >
            {customIcons.logoutIcon}
          </button>
        </div>
      </div>
      <CollapseButton isExpanded={isExpanded} toggle={toggleMenu} />
    </div>
  );
}
